import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check, Lightning, Crown, Briefcase, Gift } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  'Unlimited events',
  'EventDrop Photobooth — unlimited',
  'EventDrop Pocket Booth — unlimited',
  'EventDrop Video Guestbook — unlimited',
  'Auto-unlock on every event you create',
  'Save & reuse templates across events',
  'Priority film rendering queue',
  'No EventDrop watermarks on share pages',
  'Branding control — your logo, your colours',
];

/**
 * "For Suppliers" bottom section — Studio Pass upsell for photographers, DJs,
 * planners and venues who run multiple events per year.
 */
const SuppliersSection = () => {
  const [loading, setLoading] = useState(null); // 'annual' | 'monthly' | 'admin-free' | null
  const navigate = useNavigate();
  const { user } = useAuth();

  const startCheckout = async (plan, opts = {}) => {
    const loadingKey = opts.promoCode ? 'admin-free' : plan;
    setLoading(loadingKey);
    try {
      const payload = { plan, origin_url: window.location.origin };
      if (opts.promoCode) payload.promo_code = opts.promoCode;
      const res = await axios.post(
        `${BACKEND_URL}/api/studio-pass/checkout`,
        payload,
        { withCredentials: true },
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout — try again.');
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.info('Sign in first, then we\'ll take you to checkout.');
        navigate('/login?redirect=/studio-pass');
      } else if (e?.response?.status === 503) {
        // Stripe key not configured yet — show the actionable message to the admin/owner
        toast.error(e?.response?.data?.detail || 'Studio Pass checkout is not configured yet.', { duration: 8000 });
      } else {
        toast.error(e?.response?.data?.detail || 'Checkout failed — try again.');
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <section className="py-20 relative" data-testid="suppliers-section">
      {/* Soft gradient backdrop to separate from the rest of the page */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(700px 360px at 20% 10%, rgba(184,151,98,0.12), transparent 70%), radial-gradient(900px 400px at 90% 90%, rgba(194,157,82,0.10), transparent 70%)',
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6">
        {/* Eyebrow */}
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <Briefcase size={16} weight="bold" className="text-white" />
          </div>
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
            For Suppliers
          </span>
        </div>

        <h2 className="text-4xl sm:text-5xl font-black leading-[1.05] mb-3 text-white">
          Built for planners, DJs &amp; venues.
        </h2>
        <p className="text-base text-gray-400 max-w-2xl mb-10">
          Run unlimited events. Reuse templates. Unlock every EventDrop tool with a single supplier subscription.
          No more per-event upsells, no watermarks, no friction — just ship great events.
        </p>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-12" data-testid="supplier-features">
          {FEATURES.map((f) => (
            <div key={f} className="flex items-start gap-3 py-2">
              <Check size={18} weight="bold" className="text-[#C29D52] flex-shrink-0 mt-0.5" />
              <span className="text-[15px] text-gray-200">{f}</span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ANNUAL — headline */}
          <div
            className="relative rounded-2xl p-6 overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #161A36, #15161B)',
              border: '1px solid rgba(194, 157, 82, 0.35)',
            }}
            data-testid="supplier-plan-annual"
          >
            <div className="absolute top-4 right-4">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={{ background: 'rgba(194,157,82,0.15)', color: '#C29D52' }}
              >
                <Lightning size={10} weight="fill" /> MOST POPULAR
              </span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Crown size={18} weight="fill" className="text-[#C29D52]" />
              <h3 className="text-lg font-bold text-white">Studio Pass — Annual</h3>
            </div>
            <div className="flex items-baseline gap-2 mt-3 mb-1">
              <span className="text-5xl font-black text-white tracking-tight">£299</span>
              <span className="text-gray-400 text-sm">/ year</span>
            </div>
            <p className="text-xs text-[#C29D52] font-semibold mb-5">Lock in this year's price · one annual invoice</p>
            <button
              onClick={() => startCheckout('annual')}
              disabled={loading !== null}
              className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:scale-[1.015] disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="supplier-cta-annual"
            >
              {loading === 'annual' ? 'Loading…' : 'Go Annual — £299'}
            </button>
            <p className="text-[11px] text-gray-500 text-center mt-2.5">
              Cancel anytime · Unlimited events for 12 months
            </p>
          </div>

          {/* MONTHLY */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            data-testid="supplier-plan-monthly"
          >
            <h3 className="text-lg font-bold text-white mb-1">Studio Pass — Monthly</h3>
            <div className="flex items-baseline gap-2 mt-3 mb-1">
              <span className="text-5xl font-black text-white tracking-tight">£22.99</span>
              <span className="text-gray-400 text-sm">/ month</span>
            </div>
            <p className="text-xs text-gray-500 font-semibold mb-5">Same features · Cancel anytime</p>
            <button
              onClick={() => startCheckout('monthly')}
              disabled={loading !== null}
              className="w-full py-3 rounded-xl font-bold text-white text-sm border border-white/15 hover:bg-white/5 transition-all disabled:opacity-60"
              data-testid="supplier-cta-monthly"
            >
              {loading === 'monthly' ? 'Loading…' : 'Start Monthly'}
            </button>
            <p className="text-[11px] text-gray-500 text-center mt-2.5">
              Pauses when you do · No commitment
            </p>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 italic mt-10">
          Already run 2+ events a year? This pays for itself instantly.
        </p>

        {/* Admin-only: pre-apply OWNERFREE001 promo + skip the manual code entry */}
        {user?.is_admin && (
          <div
            className="mt-8 rounded-2xl p-5 border border-dashed border-amber-400/30 bg-amber-400/5"
            data-testid="admin-free-pass-card"
          >
            <div className="flex items-center gap-2 mb-1">
              <Gift size={14} weight="fill" className="text-amber-300" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300">
                Admin only · Comp Studio Pass
              </span>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Mints a free Studio Pass with <code className="text-amber-200">OWNERFREE</code> pre-applied.
              No card needed — the supplier just confirms the email and gets activated.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => startCheckout('annual', { promoCode: 'OWNERFREE' })}
                disabled={loading !== null}
                className="py-2.5 rounded-xl text-sm font-bold text-amber-100 border border-amber-400/40 hover:bg-amber-400/10 transition-all disabled:opacity-60"
                data-testid="admin-free-annual"
              >
                {loading === 'admin-free' ? 'Creating…' : 'Free — Annual'}
              </button>
              <button
                onClick={() => startCheckout('monthly', { promoCode: 'OWNERFREE' })}
                disabled={loading !== null}
                className="py-2.5 rounded-xl text-sm font-bold text-amber-100 border border-amber-400/40 hover:bg-amber-400/10 transition-all disabled:opacity-60"
                data-testid="admin-free-monthly"
              >
                {loading === 'admin-free' ? 'Creating…' : 'Free — Monthly'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SuppliersSection;
